// DocumentMarkerManager.js
// verwaltet alle Marker, die Dokument-Verortungen darstellen, inklusive Cluster-Handling

// Notiz-Append-Start
// Code-Richtlinie: Umgang mit optionalen Methoden wie marker.setActiveState
// ------------------------------------------------------------
// Verwende den optional chaining operator (?.()) nur dann, wenn stille Toleranz erwünscht ist.
// Beispiel: marker.setActiveState?.(true)
//
// Wenn du sicherstellen willst, dass Methoden existieren oder Fehler sichtbar werden:
// if (typeof marker.setActiveState === 'function') {
//     marker.setActiveState(true);
// } else if (import.meta.env.DEV) {
//     console.warn('Missing setActiveState handler for marker', marker);
// }
// Alternativ: publish Dev-Warnung über mediator:
// mediator.publish('marker:missingActiveHandler', { marker });
// ------------------------------------------------------------
// Notiz-Append-Ende

import { mediator } from "../../../mediator/mediatorInstance.js";

import { MainMapMarkerManager } from './MainMapMarkerManager.js'
import { setMarkerActiveState } from '../markerActiveState.js'

export class DocumentMarkerManager extends MainMapMarkerManager {
    markerFactory;
    #markerClusterer;

    constructor(mapAdapter, markerFactory) {
        super(mapAdapter);
        this.markerFactory = markerFactory;
    }

    setMarkerClusterer(markerClusterer) {
        this.#markerClusterer = markerClusterer;
    }

    addMarkers(markerDataArray) {
        this.clearMarkersByType('document');

        for (const data of markerDataArray) {
            const marker = this.markerFactory.createDocumentMarker(this.mapAdapter.getMapInstance(), data);

            marker.setActiveState = function(isActive) {
                setMarkerActiveState(marker, isActive);
            };

            marker.customId = data.id; // eindeutige ID z.B. Dokument-ID
            this._registerMarkerEvents(marker);

            this.addMarker(
                marker,
                data.id,                 // ID
                'document',              // Marker-Typ
                [data.id],               // docIds als Array
                marker.position || null  // Position, falls vorhanden
            );
        }
        this._createMarkerClusterer();
        mediator.publish('map:fit:main', {});
    }

    rotateMarkers() {
        const markers = this.getMarkersByType('document');
        for(const marker of markers) {
            this.markerFactory.setRotatedState(marker.marker);
        }
    }

    _createMarkerClusterer() {
        if(this.#markerClusterer) {
            this.#markerClusterer.addMarkers(this.getMarkersByType('document').map(entry => entry.marker));
        }
    }

    _registerMarkerEvents(marker) {
        marker.addListener('gmp-click', () => {
            mediator.publish('marker:document:click', { marker });
        });

        if(marker.content) {
            marker.content.addEventListener('mouseover', (event) => {
                const rect = event.currentTarget.getBoundingClientRect();
                mediator.publish('marker:document:mouseover', { marker, rect });
            });
            marker.content.addEventListener('mouseout', () => {
                mediator.publish('marker:document:mouseout', { marker });
            });
        }
    }

    clearMarkers() {
        if(this.#markerClusterer){
            this.#markerClusterer.clearMarkers();
        }
        super.clearAllMarkers();
    }

    // Cluster-Leak-Schutz (Phase 7b): nach jedem Re-Clustering die 'cluster'-Registry-Einträge entfernen,
    // deren Marker nicht mehr in der aktuellen Cluster-Liste der Lib steht (= alte Cluster-Leichen).
    // Quelle der Wahrheit = clusterer.clusters (bei 'clusteringend' bereits aktuell) — robust gegen das
    // per requestAnimationFrame verzögerte setMap(null) der Lib. Entfernt nur unsere Registry-Referenz,
    // die eigentliche Map-Entfernung macht die Lib selbst.
    pruneStaleClusters() {
        if (!this.#markerClusterer) { return; }
        const live = new Set(this.#markerClusterer.getClusters().map(c => c.marker));

        for (const [id, entries] of this.markerMap.entries()) {
            const remaining = entries.filter(e => !(e.type === 'cluster' && !live.has(e.marker)));
            if (remaining.length === 0) {
                this.markerMap.delete(id);
            } else if (remaining.length !== entries.length) {
                this.markerMap.set(id, remaining);
            }
        }
    }

    highlightMarkerById(id, active = true) {
        const entries = this.getMarkerById(id);
        if (!entries) return;

        const markerList = Array.isArray(entries) ? entries : [entries]; // fallback
        for (const entry of markerList) {
            const marker = entry.marker;
            if (typeof marker.setActiveState === 'function') {
                marker.setActiveState(active);
            } else if (import.meta.env.DEV) {
                console.warn('Missing setActiveState handler for marker', marker);
            }
        }
    }

    highlightClusterContainingDocument(docId, active = true) {
        const clusterEntries = this.getMarkersByType('cluster');

        for(const entry of clusterEntries) {
            const marker = entry.marker;

            // Veraltete (aus dem DOM gelöste) Cluster-Leichen überspringen — sonst trifft das
            // frühe break eine tote Instanz und der lebende Cluster wird nie erreicht.
            if (!marker.content?.isConnected) { continue; }
            if (!entry.docIds.includes(docId)) { continue; }

            setMarkerActiveState(marker, active);
        }
    }

    highlightMarkerContainingDocument(documentId, active = true) {
        this.highlightMarkerById(documentId, active);
        this.highlightClusterContainingDocument(documentId, active);
    }

    /**
     * Callback-Funktion für das Clusterer-Rendering
     * diese Methode wird in der Clusterer-Konfiguration als `render` übergeben.
     */
    renderCustomClusterMarker = ({ markers, position }) => {
        const mapInstance = this.mapAdapter.getMapInstance();
        const clusterMarker = this.markerFactory.createDocumentClusterMarker(mapInstance, position, markers.length);

        const docIds = markers.map(m => m.customId);
        const markerId = `cluster_${position.lat()}_${position.lng()}`;

        if (clusterMarker.content) {
            clusterMarker.content.dataset.docIds = docIds.join(',');
            clusterMarker.content.addEventListener('mouseover', (event) => {
                mediator.publish('marker:cluster:mouseover', {
                    clusterMarker,
                    event
                });
            });
            clusterMarker.content.addEventListener('mouseout', () => {
                mediator.publish('marker:cluster:mouseout', {
                    clusterMarker,
                    docIds
                });
            });
        }

        this.addMarker(clusterMarker, markerId, 'cluster', docIds, position);
        return clusterMarker;
    };

    getAllVisibleMarkers() {
        return [
            ...this.getMarkersByType('document').map(entry => entry.marker),
            ...this.getMarkersByType('cluster').map(entry => entry.marker)
        ];
    }
}